<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import { Text } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';

	type MenuItem = {
		type: string;
		label: string;
		subtitle?: string;
		icon: string;
		color?: string;
		label_transalte_key?: string;
		disabled?: boolean;
	};

	const dispatch = createEventDispatcher<{
		select: string;
		close: void;
	}>();

	export let placement: 'above' | 'below' | 'left' = 'above';
	export let width = 140;
	export let items: MenuItem[] = [
		{
			type: 'device',
			label: 'Device',
			label_transalte_key: 'ai_chat_context_device',
			icon: 'fa-solid fa-plug',
			color: '#24d38c',
		},
		{
			type: 'room',
			label: 'Room',
			label_transalte_key: 'ai_chat_context_room',
			icon: 'fa-solid fa-door-open',
			color: '#2196f3',
		},
		{
			type: 'group',
			label: 'Group',
			label_transalte_key: 'ai_chat_context_group',
			icon: 'fa-solid fa-layer-group',
			color: '#ffc107',
		},
		{
			type: 'scene',
			label: 'Scene',
			label_transalte_key: 'ai_chat_context_scene',
			icon: 'fa-solid fa-bolt',
			color: '#d230ff',
		},
	];

	let menuEl: HTMLDivElement;

	function handleSelect(type: string, disabled = false, event?: MouseEvent) {
		if (disabled) {
			event?.preventDefault();
			return;
		}
		dispatch('select', type);
	}

	function handleClickOutside(event: MouseEvent) {
		if (menuEl && !menuEl.contains(event.target as Node)) {
			dispatch('close');
		}
	}

	onMount(() => {
		setTimeout(() => {
			document.addEventListener('click', handleClickOutside);
		}, 0);
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	});
</script>

<div
	class="token-type-menu"
	class:below={placement === 'below'}
	class:left={placement === 'left'}
	bind:this={menuEl}
	style={`width: ${width}px;`}
>
	{#each items as item, i (item.type)}
		<button
			class="menu-item"
			class:disabled={!!item.disabled}
			class:last={i === items.length - 1}
			on:click={(e) => handleSelect(item.type, !!item.disabled, e)}
			aria-disabled={item.disabled ? 'true' : 'false'}
			type="button"
		>
			<i class={item.icon} style="color: {item.color || '#fff'}" aria-hidden="true"></i>
			<div class="menu-item-copy">
				<Text styleType="body-small" element="span" class="menu-item-label"
					>{$format(item.label_transalte_key, { default: item.label })}</Text
				>
				{#if item.subtitle}
					<Text
						styleType="body-extra-small"
						element="span"
						class="menu-item-subtitle text-color-muted">{item.subtitle}</Text
					>
				{/if}
			</div>
		</button>
	{/each}
</div>

<style>
	.token-type-menu {
		position: absolute;
		bottom: calc(100% + 8px);
		left: 0;
		width: 120px;
		background: #242527;
		border: 1px solid #5b5f66;
		border-radius: 12px;
		box-shadow: 0px 0px 25px 0px rgba(0, 0, 0, 0.75);
		overflow: hidden;
		z-index: 5000;
	}

	.token-type-menu.below {
		bottom: auto;
		top: calc(100% + 8px);
	}

	.token-type-menu.left {
		bottom: auto;
		top: 0;
		left: auto;
		right: calc(100% + 8px);
	}

	.menu-item {
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;
		min-height: 44px;
		padding: 12px;
		background: var(--color-background-1, #151617);
		border: none;
		border-bottom: 1px solid #5b5f66;
		color: #fff;
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 400;
		line-height: 19px;
		text-align: left;
		cursor: pointer;
		transition: background-color 0.15s;
	}

	.menu-item:hover {
		background: var(--color-background-2, #242527);
	}

	.menu-item.disabled,
	.menu-item[aria-disabled='true'] {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.menu-item.last {
		border-bottom: none;
	}

	.menu-item i {
		font-size: 16px;
		width: 20px;
		text-align: center;
		flex-shrink: 0;
		margin-top: 2px;
	}

	.menu-item:focus-visible {
		background: #3a3a3a;
	}

	.menu-item-copy {
		display: flex;
		flex-direction: column;
		gap: 2px;
		min-width: 0;
	}

	.menu-item-label {
		color: #fff;
	}

	.menu-item-subtitle {
		opacity: 0.85;
	}
</style>
